import { ExternalHyperlink, TextRun } from 'docx';

type DocxInlineNode = TextRun | ExternalHyperlink;

interface InlineContext {
  isBold: boolean;
  isItalic: boolean;
}

/** Typography config for text runs */
export interface RunConfig {
  fontFamily: string;
  fontSize: number; // Font size in half-points
  color: string; // Hex color without leading hash
}

/** Parsed paragraph with runs and a blockquote flag for left-border styling */
export interface ParsedParagraph {
  runs: DocxInlineNode[];
  isBlockquote: boolean;
}

const DEFAULT_CONTEXT: InlineContext = { isBold: false, isItalic: false };

/**
 * Converts an HTML string into parsed paragraph descriptors
 * Supports p, div, strong, b, em, i, u, a, ul, ol and blockquote tags
 * Must only be called client-side as it relies on the native DOMParser
 */
export const parseRichText = (
  html: string,
  runConfig: RunConfig,
): ParsedParagraph[] => {
  if (!html) return [];

  const { fontFamily, fontSize, color } = runConfig;

  /** Creates a styled TextRun from the current inline context */
  const createRun = (text: string, ctx: InlineContext) =>
    new TextRun({
      text,
      font: fontFamily,
      size: fontSize,
      color,
      bold: ctx.isBold,
      italics: ctx.isItalic,
    });

  /** Recursively maps DOM children to docx inline nodes */
  const traverseChildren = (
    children: NodeListOf<ChildNode> | ChildNode[],
    ctx: InlineContext,
  ): DocxInlineNode[] =>
    Array.from(children).flatMap((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        return [createRun((child as Text).data, ctx)];
      }

      if (child.nodeType !== Node.ELEMENT_NODE) return [];

      const el = child as Element;
      const tag = el.tagName.toLowerCase();

      switch (tag) {
        case 'strong':
        case 'b':
          return traverseChildren(el.childNodes, { ...ctx, isBold: true });

        case 'em':
        case 'i':
          return traverseChildren(el.childNodes, { ...ctx, isItalic: true });

        case 'a': {
          const href = el.getAttribute('href') ?? '';

          const innerRuns = traverseChildren(el.childNodes, ctx).filter(
            (n): n is TextRun => n instanceof TextRun,
          );

          if (!href) return innerRuns;

          return [
            new ExternalHyperlink({
              link: href,
              children: innerRuns.length ? innerRuns : [createRun(href, ctx)],
            }),
          ];
        }

        // u and unknown tags are pass-through to preserve nested inline content
        default:
          return traverseChildren(el.childNodes, ctx);
      }
    });

  /** Builds a ParsedParagraph from block-level children and pushes it if non-empty */
  const pushBlock = (
    target: ParsedParagraph[],
    children: NodeListOf<ChildNode> | ChildNode[],
    isBlockquote: boolean,
  ): void => {
    const runs = traverseChildren(children, DEFAULT_CONTEXT);
    if (runs.length) target.push({ runs, isBlockquote });
  };

  const processedHtml = html.trim().startsWith('<') ? html : `<p>${html}</p>`;
  const { body } = new DOMParser().parseFromString(processedHtml, 'text/html');

  const paragraphs: ParsedParagraph[] = [];

  for (const rawNode of Array.from(body.childNodes)) {
    if (rawNode.nodeType === Node.TEXT_NODE) {
      const text = (rawNode as Text).data?.trim();

      if (text)
        paragraphs.push({
          runs: [createRun(text, DEFAULT_CONTEXT)],
          isBlockquote: false,
        });

      continue;
    }

    if (rawNode.nodeType !== Node.ELEMENT_NODE) continue;

    const el = rawNode as Element;
    const tag = el.tagName.toLowerCase();

    if (tag === 'p' || tag === 'div') {
      pushBlock(paragraphs, el.childNodes, false);
      continue;
    }

    if (tag === 'ul' || tag === 'ol') {
      const isOrdered = tag === 'ol';
      let counter = 0;

      // Use direct children only as querySelectorAll is recursive and would
      // double-count li elements nested inside child lists
      const items = Array.from(el.children).filter(
        (c): c is HTMLLIElement => c.tagName.toLowerCase() === 'li',
      );

      for (const li of items) {
        if (isOrdered) counter++;

        const prefix = isOrdered ? `${counter}. ` : '•  ';

        const runs = traverseChildren(li.childNodes, DEFAULT_CONTEXT);

        paragraphs.push({
          runs: [createRun(prefix, DEFAULT_CONTEXT), ...runs],
          isBlockquote: false,
        });
      }

      continue;
    }

    if (tag === 'blockquote') {
      const blockChildren = Array.from(el.childNodes);

      const isBlockParagraph = (c: ChildNode): c is Element => {
        if (c.nodeType !== Node.ELEMENT_NODE) return false;
        const childTag = (c as Element).tagName.toLowerCase();
        return childTag === 'p' || childTag === 'div';
      };

      const innerBlocks = blockChildren.filter(isBlockParagraph);

      // When the blockquote wraps multiple block elements treat each independently
      // otherwise treat the whole content as a single quoted paragraph
      if (innerBlocks.length) {
        innerBlocks.forEach((child) => {
          pushBlock(paragraphs, child.childNodes, true);
        });
      } else {
        pushBlock(paragraphs, el.childNodes, true);
      }

      continue;
    }

    // Unknown block-level tags are treated as plain paragraphs
    pushBlock(paragraphs, el.childNodes, false);
  }

  return paragraphs;
};
